import { push } from "connected-react-router";
import { fork, put, takeLatest } from "redux-saga/effects";

import { callApi } from "../../../sagas/helpers/api";
import { setError, setSuccess, startLoading } from "../../../store/appSlice";
import * as CashManagementProviderCustomerConfigApi from "../api/ProviderCustomerConfig";
import {
  createProviderCustomerConfig,
  fetchProviderCustomerConfig,
  fetchProviderCustomerConfigs,
  providerCustomerConfigOperationPaths,
  setProviderCustomerConfig,
  setProviderCustomerConfigs,
  updateProviderCustomerConfig,
} from "./providerCustomerConfigs.slice";

import type { UUID } from "../../../utils";
import type {
  CashManagementProviderCustomerConfig,
  CashManagementProviderCustomerConfigCredentials,
  ClearJunctionCredentials,
} from "../models/ProviderCustomerConfig";

function* fetchProviderCustomerConfigsSaga({
  payload,
}: {
  payload: {
    customerId: UUID;
  };
  type: string;
}) {
  const { customerId } = payload;
  const { listing: operationPath } = providerCustomerConfigOperationPaths({
    customerId,
  });

  yield put(startLoading(operationPath));

  try {
    // @ts-ignore noImplicitAny
    const data: any = yield callApi(
      CashManagementProviderCustomerConfigApi.getCashManagementProviderCustomerConfigs,
      customerId
    );

    yield put(setProviderCustomerConfigs({ list: data }));
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* fetchProviderCustomerConfigSaga({
  payload,
}: {
  payload: {
    configId: UUID;
  };
  type: string;
}): Generator<any> {
  const { configId } = payload;
  const { details: operationPath } = providerCustomerConfigOperationPaths({
    configId,
  });

  yield put(startLoading(operationPath));

  try {
    const data: any = yield callApi(
      CashManagementProviderCustomerConfigApi.getCashManagementProviderCustomerConfig,
      configId
    );

    yield put(
      setProviderCustomerConfig({
        id: { id: configId },
        item: data,
      })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* createProviderCustomerConfigSaga({
  payload,
}: {
  payload: {
    customerId: UUID;
    providerId: UUID;
    data: CashManagementProviderCustomerConfigCredentials;
  };
  type: string;
}): Generator<any> {
  const { customerId, providerId, data: config } = payload;
  const { create: operationPath } = providerCustomerConfigOperationPaths({
    customerId,
    providerId,
  });

  yield put(startLoading(operationPath));
  try {
    const data: any = yield callApi(
      CashManagementProviderCustomerConfigApi.postCashManagementProviderCustomerConfig,
      customerId,
      {
        cashManagementProvider: { id: providerId },
        ...((config as ClearJunctionCredentials).riskScore
          ? { riskScore: (config as ClearJunctionCredentials).riskScore }
          : { credentials: config }),
      } as CashManagementProviderCustomerConfig
    );

    yield put(
      setProviderCustomerConfig({
        id: { id: data.id },
        item: data,
      })
    );
    yield put(
      push(
        `/configuration/customers/${customerId}/cash-management/provider-config/${data.id}`
      )
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* updateProviderCustomerConfigSaga({
  payload,
}: {
  payload: {
    configId: UUID;
    providerId: UUID;
    data: CashManagementProviderCustomerConfigCredentials;
  };
  type: string;
}): Generator<any> {
  const { configId, data: config, providerId } = payload;
  const { update: operationPath } = providerCustomerConfigOperationPaths({
    configId,
  });

  yield put(startLoading(operationPath));
  try {
    const data: any = yield callApi(
      CashManagementProviderCustomerConfigApi.putCashManagementProviderCustomerConfig,
      configId,
      {
        cashManagementProvider: { id: providerId },
        ...((config as ClearJunctionCredentials).riskScore
          ? { riskScore: (config as ClearJunctionCredentials).riskScore }
          : { credentials: config }),
      } as CashManagementProviderCustomerConfig
    );

    yield put(
      setProviderCustomerConfig({
        id: { id: configId },
        item: data,
      })
    );
    yield put(
      push(
        `/configuration/customers/${data.customerId}/cash-management/provider-config/${configId}`
      )
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

function* watchFetchProviderCustomerConfigs() {
  yield takeLatest(
    fetchProviderCustomerConfigs.type,
    fetchProviderCustomerConfigsSaga
  );
}

function* watchFetchProviderCustomerConfig() {
  yield takeLatest(
    fetchProviderCustomerConfig.type,
    fetchProviderCustomerConfigSaga
  );
}

function* watchCreateProviderCustomerConfig() {
  yield takeLatest(
    createProviderCustomerConfig.type,
    createProviderCustomerConfigSaga
  );
}

function* watchUpdateProviderCustomerConfig() {
  yield takeLatest(
    updateProviderCustomerConfig.type,
    updateProviderCustomerConfigSaga
  );
}

export default function* rootSaga() {
  yield fork(watchFetchProviderCustomerConfigs);
  yield fork(watchFetchProviderCustomerConfig);
  yield fork(watchCreateProviderCustomerConfig);
  yield fork(watchUpdateProviderCustomerConfig);
}
